import {BehaviorSubject, combineLatest, debounceTime, Observable, tap} from 'rxjs';
import {V2TeachersPagesHomeSectionsTestsController} from '@esgi/contracts/esgi';
import {ReportTest} from '@esgi/main/kits/reports';
import {storage, SubjectLevel, SubjectTab, SubjectType} from '@esgi/main/libs/store';
import {TestContentArea} from '@esgi/main/kits/common';
import {BaseService} from '@esgi/core/service';
import {createParentLetterSettingsForm} from './form';
import {TestSession} from './types';
import {parentLetterSettingsMock, sessionsMock} from './test-mocks';
import {SubjectType as SubjectTypeContract} from '@esgi/contracts/esgi/types/esgi.enums/subject-type';

export class ParentLetterService extends BaseService {
	public tests$ = new BehaviorSubject<ReportTest[]>(null);
	public isSubjectsDataLoaded = new BehaviorSubject(false);
	public isTestDataLoaded = new BehaviorSubject(false);
	public selectedStudentID = new BehaviorSubject<number>(null);
	public selectedSubject = new BehaviorSubject<SubjectTab>(null);
	public selectedTestIDs = new BehaviorSubject<number[]>(null);
	public subjects = new BehaviorSubject<SubjectTab[]>([]);
	public form = createParentLetterSettingsForm();
	public loading = new BehaviorSubject<boolean>(false);

	private controller = new V2TeachersPagesHomeSectionsTestsController();
	private subjectStorage = storage.subjects();

	constructor() {
		super();

		this.subjectStorage.get().subscribe(subjects => {
			this.subjects.next(subjects.filter(subject => !subject.hidden && subject.type === SubjectType.Personal && subject.level === SubjectLevel.Teacher));

			if(!this.isSubjectsDataLoaded.value) {
				this.isSubjectsDataLoaded.next(true);
			}
		});
	}

	public init = () => {
		return this.completeOnDestroy(combineLatest([this.selectedStudentID, this.selectedSubject]))
			.pipe(debounceTime(100),
				tap(([studentID, subject]) => {
					this.isTestDataLoaded.next(false);
					if (studentID && subject) {
						return this.getTestCardsByStudent(Number(subject.id), studentID).subscribe({
							next: () => this.initSettings(),
							complete: () => this.isTestDataLoaded.next(true),
						});
					}

					this.tests$.next([]);
				}),
			);
	};

	public report = () => {
		const selectedTests = this.tests$.value.filter(test => this.selectedTestIDs.value.includes(test.id));

		const model = {
			includeSkippedQuestions: this.form.controls.isIncludeSkippedQuestions.value,
			showSummary: this.form.controls.isShowSummary.value,
			showSessionDate: this.form.controls.isShowSessionDate.value,
			showNotes: this.form.controls.isShowNotes.value,
			includeGrades: this.form.controls.isIncludeGrades.value,
			includeTheParentMessage: this.form.controls.isIncludeParentMessage.value,
			tests: selectedTests,
		};

		return new Observable(subscriber => {
			subscriber.next(model);
			subscriber.complete();
		}).pipe(tap((response) => {
			console.log('response', response);
		}));
	};

	public updateTestCardsData = (testID: number, lastSession: TestSession) => {
		const tests = this.tests$.value.map(test => test.id === testID ? {...test, lastSession} : test);
		this.tests$.next(tests);
	};

	private initSettings = () => {
		const {
			includeSkippedQuestions,
			includeParentMessage,
			includeGrades,
			showSummary,
			showSessionDate,
			showNotes,
		} = parentLetterSettingsMock;

		this.form.value = {
			isIncludeSkippedQuestions: includeSkippedQuestions,
			isShowSummary: showSummary,
			isShowSessionDate: showSessionDate,
			isShowNotes: showNotes,
			isIncludeGrades: includeGrades,
			isIncludeParentMessage: includeParentMessage,
		};
	};

	private getTestCardsByStudent = (subjectID: number, studentID: number) => {
		return this.controller.testCardsByStudent({
			subjectID,
			subjectType: SubjectTypeContract.Personal,
			studentsIDs: [9900276, 9900290, 9900310, 9900318, 9900324, 9900332, 9900337, 9900349, 9900358, 9900367, 9900376, 9900386, 9900392, 9900400, 9900404, 9900408, 9900417, 9900423],
			studentID,
		}).pipe(tap(({testCards}) => {
			const value = testCards.map(({testInfo}, index) => ({
				id: testInfo.id,
				name: testInfo.name,
				contentArea: TestContentArea[testInfo.contentArea],
				correct: 1 + index,
				incorrect: 2 + index,
				untested: 3 + index,
				sessions: sessionsMock,
				lastSession: sessionsMock[sessionsMock.length - 1],
			}));
			this.tests$.next(value);
		}));
	};
}
